import type { OrganizationMemberDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import { Switch } from '@sara/ui/components/switch';

export type MemberRole = OrganizationMemberDto['role'];

interface TeamMemberListProps {
  members: OrganizationMemberDto[];
  currentUserId: string | null;
  canManage: boolean;
  onChangeRole: (member: OrganizationMemberDto, role: MemberRole) => void;
  changingRoleId: string | null;
  onRemove: (member: OrganizationMemberDto) => void;
  /** Staff-only: when true, a super-admin toggle column is shown per member. */
  canManageSuperAdmin?: boolean;
  onToggleSuperAdmin?: (member: OrganizationMemberDto, isSuperAdmin: boolean) => void;
  togglingSuperAdminUserId?: string | null;
}

const ROLE_LABELS: Record<MemberRole, string> = {
  ADMIN: 'Admin',
  MEMBER: 'Member',
};

function formatDate(value: string | Date | null | undefined) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function TeamMemberList({
  members,
  currentUserId,
  canManage,
  onChangeRole,
  changingRoleId,
  onRemove,
  canManageSuperAdmin = false,
  onToggleSuperAdmin,
  togglingSuperAdminUserId = null,
}: TeamMemberListProps) {
  // The super-admin toggle adds a column, so the grid template is staff-aware.
  const columnsClass = canManageSuperAdmin
    ? 'grid-cols-[1.6fr_1.2fr_9rem_7rem_6.5rem]'
    : 'grid-cols-[1.6fr_1.2fr_9rem_6.5rem]';
  const columnTemplate = canManageSuperAdmin
    ? '1.6fr 1.2fr 9rem 7rem 6.5rem'
    : '1.6fr 1.2fr 9rem 6.5rem';

  return (
    <DataPanel>
      <DataPanel.Header columns={columnTemplate}>
        <span>Name</span>
        <span>Email</span>
        <span>Role</span>
        {canManageSuperAdmin && <span>Super Admin</span>}
        <span className="sr-only">Actions</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {members.map((member) => {
            const isSelf = member.userId !== null && member.userId === currentUserId;
            const name =
              `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email;
            const joined = formatDate(member.createdAt);

            return (
              <li
                key={member.id}
                data-testid="team-member-row"
                className={`grid ${columnsClass} items-center gap-6 px-6 py-5`}
              >
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-medium tracking-tight text-foreground">
                    {name}
                    {isSelf && <span className="ml-1.5 text-muted-foreground">(you)</span>}
                  </p>
                  <p className="mt-1 text-xs tabular-nums text-muted-foreground">Joined {joined}</p>
                </div>
                <span className="truncate text-[13px] text-muted-foreground">{member.email}</span>
                {canManage ? (
                  <Select
                    value={member.role}
                    onValueChange={(role) => {
                      if (role !== member.role) onChangeRole(member, role as MemberRole);
                    }}
                    disabled={changingRoleId === member.id}
                  >
                    <SelectTrigger
                      className="h-8 w-[7.5rem] text-[13px]"
                      aria-label={`Role for ${name}`}
                      data-testid="member-role-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                      <SelectItem value="MEMBER">Member</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[13px] text-muted-foreground">
                    {ROLE_LABELS[member.role]}
                  </span>
                )}
                {canManageSuperAdmin && (
                  <div className="flex justify-start">
                    <Switch
                      checked={member.isSuperAdmin}
                      // Can't toggle a pending invite (no user) or demote yourself
                      // (matches the backend guardrail).
                      disabled={
                        member.userId === null ||
                        isSelf ||
                        togglingSuperAdminUserId === member.userId
                      }
                      onCheckedChange={(checked) => onToggleSuperAdmin?.(member, checked)}
                      aria-label={`Super admin for ${name}`}
                      data-testid="member-super-admin-toggle"
                    />
                  </div>
                )}
                <div className="shrink-0 justify-self-end">
                  {canManage && (
                    <Button
                      variant="outline"
                      shape="pill"
                      size="sm"
                      onClick={() => onRemove(member)}
                      data-testid="remove-member"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </span>
      </DataPanel.Footer>
    </DataPanel>
  );
}
